import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { find, sumBy, isUndefined } from "lodash";
import { formatNumber, formatCurrency, CurrencySymbol, currencyMap } from "services/util";
import { withProps } from "recompose";
import n2words from 'n2words'
import CurrencyConversion from "./CurrencyConversion";

const TaxPercent = ({ lineItem: { TaxType }, taxRates }) => {
  const taxType = find(taxRates, { TaxType });
  return taxType ? `${taxType.EffectiveRate}%` : null;
};

export const LineItems = ({
  invoice,
  taxRates,
  history,
  organisation = {},
  includeDiscount,
  lang,
  lineItemHeaderStyle,
  lineItemStyle,
  subtotalLineStyle,
  taxLineStyle,
  totalLineStyle,
}) => {

  const { BaseCurrency } = organisation
  const isForeignCurrency = !!BaseCurrency && (BaseCurrency !== invoice.CurrencyCode)
  includeDiscount = invoice.LineItems?.some((line) => line.DiscountRate)
  var taxIncluded = invoice.LineAmountTypes === "Inclusive" ? true : false
  const taxes = {};
  for (let i = 0; i < invoice.LineItems.length; i++) {
    const taxRate = taxRates.find((tr) => tr?.TaxType === invoice.LineItems[i].TaxType)?.EffectiveRate;
    if (!taxes[taxRate]) {
      taxes[taxRate] = 0;
    }
    taxes[taxRate] += parseFloat(invoice.LineItems[i].TaxAmount);
  }
  return (
    <div className="">
      <table className="table table-sm table--lineitems">
        <thead>
          <tr style={lineItemHeaderStyle}>
            <th>
              <Trans>Description</Trans>
            </th>
            <th className="text-center">
              <Trans>Quantity</Trans>
            </th>
            <th className="text-center">
              <Trans>Unit Price</Trans>
            </th>
            {includeDiscount && (
              <th className="text-center">
                <Trans>Discount %</Trans>
              </th>
            )}
            <th className="text-center">
              <Trans>Without Tax</Trans>
            </th>
            <th className="text-center">
              <Trans>Tax Rate</Trans>
            </th>
            <th className="text-center">
              <Trans>Tax Amount</Trans>
            </th>
            <th className="text-center">
              <Trans>Amount</Trans>
            </th>
            {isForeignCurrency &&
              <th className="text-center">
                <Trans>Amount in</Trans> <CurrencySymbol currency={BaseCurrency} />
              </th>
            }
          </tr>
        </thead>
        <tbody style={lineItemStyle}>
          {invoice.LineItems?.map((i, idx) => (
            <tr key={idx}>
              <td style={{ maxWidth: 250 }}>{i.Item?.Name ? (i.Item.Name === i.Description ? i.Item.Name : i.Item.Name + (i.Description && " - " + i.Description)) : i.Description}</td>
              <td className="text-center">{formatNumber(i.Quantity, "0,0.00[0000]")}</td>
              <td className="text-center">{formatCurrency(i.UnitAmount, "0,0.00[0000]")}</td>
              {includeDiscount && (
                <td className="text-center">
                  {i.DiscountRate ? `${i.DiscountRate}%` : null}
                </td>
              )}
              <td className="text-center">
                {taxIncluded ?
                  formatCurrency(parseFloat(i.LineAmount) - parseFloat(i.TaxAmount))
                  :
                  formatCurrency(i.LineAmount)
                }
              </td>

              <td className="text-center">
                <TaxPercent taxRates={taxRates} lineItem={i} />
              </td>
              <td className="text-center">{formatCurrency(i.TaxAmount)}</td>
              <td className="text-center">
                {taxIncluded ?
                  formatCurrency(i.LineAmount)
                  :
                  formatCurrency(parseFloat(i.LineAmount) + parseFloat(i.TaxAmount))
                }
              </td>
              {isForeignCurrency &&
                <td className="text-center">
                  {taxIncluded ?
                    formatCurrency(i.LineAmount / invoice.CurrencyRate || 0)
                    :
                    formatCurrency((parseFloat(i.LineAmount) + parseFloat(i.TaxAmount)) / invoice.CurrencyRate || 0)
                  }
                </td>
              }
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row justify-content-end">
        <div className="col-auto">
          <table className="table table-sm table--lineitems">
            <tbody>
              <tr style={subtotalLineStyle}>
                <td className="text-start text-nowrap border-top-0" colSpan="2">
                  <Trans>Subtotal</Trans>
                  {Number(invoice.TotalDiscount) > 0 ? (
                    <>
                      {" "}
                      <Trans>
                        (includes a discount of{" "}
                        {{ amount: formatCurrency(invoice.TotalDiscount) }})
                      </Trans>
                    </>
                  ) : null}
                </td>
                <td className="text-nowrap border-top-0 d-flex w-100 justify-content-end">
                  <div dir="ltr">
                    <CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(invoice.SubTotal)} />
                  </div>
                </td>
              </tr>
              {Object.entries(taxes).filter(([rate, total]) => parseFloat(total) !== 0).map(([rate, total]) => (
                <tr style={taxLineStyle}>
                  <td className="text-start text-nowrap" colSpan="2">
                    <Trans>Total Tax</Trans>
                    <span className="mx-1">({rate}%)</span>
                  </td>
                  <td className="text-start d-flex w-100 justify-content-end">
                    <div dir="ltr">
                      <CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(total)} />
                    </div>
                  </td>
                </tr>
              ))}
              <tr style={totalLineStyle}>
                <td className="text-start text-nowrap" colSpan="2">
                  <strong>
                    <Trans>TOTAL (Including VAT)</Trans>
                  </strong>
                </td>
                <td className="text-nowrap d-flex w-100 justify-content-end">
                  <div dir="ltr">
                    <strong><CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(invoice.Total)} /></strong>
                  </div>
                </td>
              </tr>
              <CurrencyConversion item={invoice} organisation={organisation} history={history} />
              {/* {!invoice.InvoiceID && invoice.DueDateString ? (
                <tr>
                  <td className="text-start text-nowrap" colSpan="2">
                    <strong>
                      <Trans>DUE DATE</Trans>
                    </strong>
                  </td>
                  <td className="text-right text-nowrap">
                    <strong>{invoice.DueDateString}</strong>
                  </td>
                </tr>
              ) : null} */}
              {!!(invoice.Payments && invoice.Payments.length) && (
                <tr>
                  <td className="text-start text-nowrap" colSpan="2">
                    {invoice.InvoiceID ? (
                      <Trans>Less Amount Paid</Trans>
                    ) : (
                      <Trans>Less Cash Refunded</Trans>
                    )}
                  </td>
                  <td className="d-flex w-100 justify-content-end">
                    <div dir="ltr">
                      <CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(sumBy(invoice.Payments, (i) => parseFloat(i.Amount) || 0))} />
                    </div>
                  </td>
                </tr>
              )}
              {!!(invoice.CreditNotes && invoice.CreditNotes.length) && (
                <tr>
                  <td className="text-start text-nowrap" colSpan="2">
                    <Trans>Less Amount Credited</Trans>
                  </td>
                  <td className="d-flex w-100 justify-content-end">
                    <div dir="ltr">
                      <CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(sumBy(invoice.CreditNotes, (i) => parseFloat(i.AppliedAmount) || 0))} />
                    </div>
                  </td>
                </tr>
              )}
              {!!(invoice.Overpayments && invoice.Overpayments.length) && (
                <tr>
                  <td className="text-start text-nowrap" colSpan="2">
                    <Trans>Less Overpayment Amount</Trans>
                  </td>
                  <td className="d-flex w-100 justify-content-end">
                    <div dir="ltr">
                      <CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(sumBy(invoice.Overpayments, (i) => parseFloat(i.AppliedAmount) || 0))} />
                    </div>
                  </td>
                </tr>
              )}
              {!!(invoice.Allocations && invoice.Allocations.length) && (
                <tr>
                  <td className="text-start text-nowrap" colSpan="2">
                    <Trans>Less Credit to Invoice(s)</Trans>
                  </td>
                  <td className="d-flex w-100 justify-content-end">
                    <div dir="ltr">
                      <CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(sumBy(invoice.Allocations, (i) => parseFloat(i.Amount) || 0))} />
                    </div>
                  </td>
                </tr>
              )}
              {!!(
                !isUndefined(invoice.AmountDue) && invoice.Total !== invoice.AmountDue
              ) && (
                  <tr>
                    <td className="text-start text-nowrap" colSpan="2">
                      <strong>
                        <Trans>AMOUNT DUE</Trans>
                      </strong>
                    </td>
                    <td className="d-flex w-100 justify-content-end">
                      <div dir="ltr">
                        <strong><CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(invoice.AmountDue)} /></strong>
                      </div>
                    </td>
                  </tr>
                )}
              {!isUndefined(invoice.RemainingCredit) && invoice.RemainingCredit > 0 && (
                <tr>
                  <td className="text-start text-nowrap" colSpan="2">
                    <strong>
                      <Trans>Remaining Credit</Trans>
                    </strong>
                  </td>
                  <td className="d-flex w-100 justify-content-end">
                    <div dir="ltr">
                      <strong><CurrencySymbol currency={invoice.CurrencyCode} value={formatCurrency(invoice.RemainingCredit)} /></strong>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="d-flex justify-content-end text-muted text-nowrap mt-1">
        <p><Trans>Amount in words:</Trans> {translate_it(invoice.Total, lang, invoice.CurrencyCode)}</p>
      </div>
    </div>
  );
}

export function ArabicAmount({ amount, lang, currency }) {
  const { t } = useTranslation();
  const [integerPart, decimalPart] = parseFloat(amount)?.toFixed(2).split('.');
  const integerInWords = n2words(parseInt(integerPart, 10), { lang: lang || 'ar' });
  const decimalInWords = n2words(parseInt(decimalPart, 10), { lang: lang || 'ar' });

  const currency_map = currencyMap[currency] || currencyMap['SAR'];

  let result = '';

  if (parseInt(integerPart) > 0) {
    result += `${integerInWords} ${t(currency_map.currency)}`;
  }

  if (parseInt(decimalPart) > 0) {
    if (result) result += ' ' + t('and') + ' ';
    result += `${decimalInWords} ${t(currency_map.penny)}`;
  }

  return result;
}

export default withProps(({ includeDiscount }) => ({

}))(LineItems);

const translate_it = (amount, lang, currency) => {
  try {
    return <ArabicAmount amount={amount} currency={currency} lang={lang} />
  } catch (e) {
    return <ArabicAmount amount={amount} currency={currency} lang={"en"} />
  }
};